import type { TFunction } from 'i18next'
import type { DownloadFile, DownloadFileLimits, DownloadItem, DownloadSpaceInput, DownloadsModuleConfig } from '@/types/downloads'

export const MAX_CATEGORIES = 50
export const CATEGORY_NAME_MAX = 60

const MB = 1024 * 1024

/**
 * What the API answers in `fileLimits` today (audio and video up to 4 GB): the picker and the drop zone use it until the
 * space config arrives, or when an older API leaves it out. The server checks type and size again.
 */
export const DEFAULT_FILE_LIMITS: DownloadFileLimits = {
  defaultMaxBytes: 200 * MB,
  serverUploadMaxBytes: 200 * MB,
  categories: [
    {
      category: 'audio',
      maxBytes: 4096 * MB,
      mimeTypes: ['audio/wav', 'audio/x-wav', 'audio/wave', 'audio/vnd.wave', 'audio/mpeg', 'audio/mp3', 'audio/x-mp3', 'audio/mpeg3', 'audio/x-mpeg-3'],
      extensions: ['wav', 'mp3']
    },
    { category: 'video', maxBytes: 4096 * MB, mimeTypes: ['video/mp4', 'video/mpeg'], extensions: ['mp4', 'mpeg', 'mpg'] },
    {
      category: 'other',
      maxBytes: 200 * MB,
      mimeTypes: [],
      extensions: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'xls', 'xlsx', 'ods', 'csv', 'pdf', 'doc', 'docx', 'odt', 'txt', 'md', 'markdown', 'ppt', 'pptx', 'zip']
    }
  ]
}

/** A usable config: the server's when it came with categories, the fallback otherwise. */
export const resolveFileLimits = (limits?: DownloadFileLimits | null): DownloadFileLimits =>
  limits && Array.isArray(limits.categories) && limits.categories.length > 0 ? limits : DEFAULT_FILE_LIMITS

/** Every accepted extension, for the picker's `accept`. */
export const fileAccept = (limits?: DownloadFileLimits | null): string =>
  [...new Set(resolveFileLimits(limits).categories.flatMap((c) => c.extensions))].map((e) => `.${e}`).join(',')

const EXTENSION_MIME: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ods: 'application/vnd.oasis.opendocument.spreadsheet', csv: 'text/csv', pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  odt: 'application/vnd.oasis.opendocument.text', txt: 'text/plain', md: 'text/markdown', markdown: 'text/markdown',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  zip: 'application/zip',
  wav: 'audio/wav', mp3: 'audio/mpeg', mp4: 'video/mp4', mpeg: 'video/mpeg', mpg: 'video/mpeg'
}

export const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase()
}

/**
 * The browser leaves `file.type` empty for some types (md, csv on some systems) and some systems
 * name a type the API does not list (`video/x-mpeg`); the extension fills or corrects it.
 */
export const mimeOf = (file: File): string => {
  const byExtension = EXTENSION_MIME[extensionOf(file.name)]
  const fromBrowser = file.type
  if (!fromBrowser) return byExtension || 'application/octet-stream'
  const listed = DEFAULT_FILE_LIMITS.categories.some((c) => c.mimeTypes.includes(fromBrowser))
  /* Audio and video are matched by type on the server: an unlisted spelling would be refused. */
  if (/^(audio|video)\//.test(fromBrowser) && !listed && byExtension) return byExtension
  return fromBrowser
}

/** The category whose extensions include the file's (`null` when no category takes it). */
export const categoryOf = (name: string, limits?: DownloadFileLimits | null) => {
  const ext = extensionOf(name)
  return ext ? resolveFileLimits(limits).categories.find((c) => c.extensions.includes(ext)) ?? null : null
}

/** The size ceiling for this file's type. */
export const maxBytesFor = (name: string, limits?: DownloadFileLimits | null): number => {
  const resolved = resolveFileLimits(limits)
  return categoryOf(name, resolved)?.maxBytes ?? resolved.defaultMaxBytes
}

export type FileProblem = 'tooBig' | 'type' | 'empty'

/** `null` when the file may be sent; otherwise which rule it breaks. */
export const checkFile = (file: Pick<File, 'name' | 'size'>, limits?: DownloadFileLimits | null): FileProblem | null => {
  if (file.size === 0) return 'empty'
  const category = categoryOf(file.name, limits)
  if (!category) return 'type'
  return file.size > category.maxBytes ? 'tooBig' : null
}

/** Display families: one label per product or format, in the order the screen lists them. */
const FAMILIES: Array<{ key: string; extensions: string[]; label?: string }> = [
  { key: 'mp3', extensions: ['mp3'], label: 'MP3' },
  { key: 'wav', extensions: ['wav'], label: 'WAV' },
  { key: 'mp4', extensions: ['mp4'], label: 'MP4' },
  { key: 'mpeg', extensions: ['mpeg', 'mpg'], label: 'MPEG' },
  { key: 'pdf', extensions: ['pdf'], label: 'PDF' },
  { key: 'word', extensions: ['doc', 'docx', 'odt'] },
  { key: 'excel', extensions: ['xls', 'xlsx', 'ods'] },
  { key: 'powerpoint', extensions: ['ppt', 'pptx'] },
  { key: 'csv', extensions: ['csv'], label: 'CSV' },
  { key: 'txt', extensions: ['txt', 'md', 'markdown'], label: 'TXT' },
  { key: 'images', extensions: ['png', 'jpg', 'jpeg', 'webp', 'gif'] },
  { key: 'zip', extensions: ['zip'], label: 'ZIP' }
]

/** Every extension in family order; an extension the screen does not know goes last, as it came. */
const orderExtensions = (extensions: string[]): string[] => {
  const known = FAMILIES.flatMap((f) => f.extensions)
  const rank = (e: string) => { const i = known.indexOf(e); return i < 0 ? known.length : i }
  return [...new Set(extensions)].sort((a, b) => rank(a) - rank(b))
}

export interface FileFormatGroup {
  /** `media` when audio and video share one ceiling; otherwise the API's category. */
  key: string
  maxBytes: number
  /** Family labels (`MP3`, `word`, ...): the screen translates the ones without a fixed name. */
  families: Array<{ key: string; label?: string }>
  extensions: string[]
}

/**
 * The rule as people read it, built from `fileLimits.categories`: audio and video share a line
 * when their ceiling is the same, the rest is "documents and others".
 */
export const fileFormatGroups = (limits?: DownloadFileLimits | null): FileFormatGroup[] => {
  const categories = resolveFileLimits(limits).categories.filter((c) => c.extensions.length > 0)
  const audio = categories.find((c) => c.category === 'audio')
  const video = categories.find((c) => c.category === 'video')
  const merged = audio && video && audio.maxBytes === video.maxBytes
  const raw: Array<{ key: string; maxBytes: number; extensions: string[] }> = []
  if (merged) raw.push({ key: 'media', maxBytes: audio.maxBytes, extensions: [...audio.extensions, ...video.extensions] })
  for (const c of categories) {
    if (merged && (c === audio || c === video)) continue
    raw.push({ key: String(c.category), maxBytes: c.maxBytes, extensions: c.extensions })
  }
  /* Media first: it is the line that differs from what people expect. */
  raw.sort((a, b) => Number(b.key === 'media' || b.key === 'audio' || b.key === 'video') - Number(a.key === 'media' || a.key === 'audio' || a.key === 'video'))
  return raw.map((g) => {
    const extensions = orderExtensions(g.extensions)
    const families: FileFormatGroup['families'] = []
    for (const family of FAMILIES) {
      if (family.extensions.some((e) => extensions.includes(e))) families.push({ key: family.key, label: family.label })
    }
    const knownExt = FAMILIES.flatMap((f) => f.extensions)
    for (const e of extensions) if (!knownExt.includes(e)) families.push({ key: e, label: e.toUpperCase() })
    return { key: g.key, maxBytes: g.maxBytes, families, extensions }
  })
}

/**
 * A size for a limit: whole gigabytes as GB (never "2048 MB"), the rest as MB. Binary units, the
 * same as the API's ceiling.
 */
export const formatLimit = (bytes: number, locale?: string): string => {
  const GB = 1024 * MB
  const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 })
  return bytes >= GB ? `${nf.format(bytes / GB)} GB` : `${nf.format(bytes / MB)} MB`
}

/** What `PUT /downloads/spaces/:id` takes. */
export const toSpaceInput = (config: DownloadsModuleConfig): DownloadSpaceInput => ({
  mode: config.mode ?? 'multi',
  requireApproval: config.requireApproval ?? false,
  categories: (config.categories ?? [])
    .map((c) => ({ ...(c.id ? { id: c.id } : {}), name: c.name.trim() }))
    .filter((c) => c.name)
})

export interface DownloadsConfigErrors {
  categories?: string
}

/** Categories are optional; a blank or repeated name is not. */
export const validateDownloadsConfig = (config: DownloadsModuleConfig, t: TFunction): DownloadsConfigErrors | null => {
  const names = (config.categories ?? []).map((c) => c.name.trim().toLowerCase())
  if (names.some((n) => !n)) return { categories: t('downloads:config.errors.categoryBlank') }
  if (new Set(names).size !== names.length) return { categories: t('downloads:config.errors.categoryRepeated') }
  return null
}

export const defaultDownloadsConfig = (t: TFunction): DownloadsModuleConfig => ({
  mode: 'multi',
  requireApproval: false,
  categories: [
    { name: t('downloads:config.suggestions.spreadsheets') },
    { name: t('downloads:config.suggestions.ebooks') },
    { name: t('downloads:config.suggestions.templates') }
  ]
})

export const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback

/** The `errors` array of a refused request, by field. */
export const apiFieldErrors = (err: any): Record<string, string> => {
  const list: Array<{ param?: string; message?: string }> = Array.isArray(err?.response?.data?.errors) ? err.response.data.errors : []
  const byField: Record<string, string> = {}
  for (const e of list) if (e.param && e.message && !byField[e.param]) byField[e.param] = e.message
  return byField
}

export const apiStatus = (err: any): number | undefined => err?.response?.status

/** Plain text of the editor's HTML, to tell an empty description from `<p></p>`. */
export const htmlToText = (html: string | null | undefined): string => (html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()

/** Where an item lives: `/{section}/{space}/{itemId}`. */
export const itemPath = (spacePath: string, itemId: string) => `${spacePath}/${itemId}`

/** True when the person may see the files (or the buy button is not in the way). */
export const isOpen = (item: Pick<DownloadItem, 'locked' | 'canDownload'>) => !item.locked && item.canDownload

export const MY_DOWNLOADS_PATH = '/_/downloads/mine'

/** The file's icon family, for the list row. */
export type FileKind = 'pdf' | 'sheet' | 'doc' | 'slides' | 'image' | 'zip' | 'audio' | 'video' | 'text'
export const fileKindOf = (name: string): FileKind => {
  const e = extensionOf(name)
  if (e === 'pdf') return 'pdf'
  if (['xls', 'xlsx', 'ods', 'csv'].includes(e)) return 'sheet'
  if (['doc', 'docx', 'odt'].includes(e)) return 'doc'
  if (['ppt', 'pptx'].includes(e)) return 'slides'
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(e)) return 'image'
  if (e === 'zip') return 'zip'
  if (['mp3', 'wav'].includes(e)) return 'audio'
  if (['mp4', 'mpeg', 'mpg'].includes(e)) return 'video'
  return 'text'
}

/** The player a file gets, from the API's `player` (or `playable` plus the type, on an API that sends only the flag). */
export const playerOf = (file: Pick<DownloadFile, 'playable' | 'player' | 'mimeType'>): 'audio' | 'video' | null => {
  if (file.player === 'audio' || file.player === 'video') return file.player
  if (file.playable !== true) return null
  if (file.mimeType?.startsWith('audio/')) return 'audio'
  if (file.mimeType === 'video/mp4') return 'video'
  return null
}
